import { Typography, Alert, Box } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import apiReq from 'core/api';
import { useSnackbar, globalConfirm } from '../../components/GlobalSnackbar';
import Loader from '../../components/Loader';
import GeneratedListItem from './GeneratedListItem';

export default function GeneratedList({
  selectedTechnicalTask,
  onSelectItem,
  reloadKey = 0,
  compact = false,
}) {
  const currProject = useSelector((state) => state.reducer.project.id);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedList, setGeneratedList] = useState([]);
  const showSnackbar = useSnackbar();

  const handleDownload = (item) => {
    try {
      const safeName = String(item?.name || 'tz')
        .trim()
        .replaceAll('/', '-')
        .replaceAll('\\', '-');

      const content =
        typeof item?.doc_content === 'string' && item.doc_content.trim().length > 0
          ? item.doc_content
          : JSON.stringify(item, null, 2);

      const ext =
        typeof item?.doc_content === 'string' && item.doc_content.trim().length > 0 ? 'md' : 'json';
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showSnackbar({ message: 'Не удалось скачать ТЗ', severity: 'error' });
    }
  };

  const getGeneratedList = useCallback(() => {
    setIsLoading(true);
    apiReq(`projects/specifications-history?project_id=${currProject}`, 'get')
      .then((res) => {
        const list = res?.length ? res : [];
        setGeneratedList(list);
      })
      .catch(() => {
        showSnackbar({ message: 'Ошибка загрузки списка', severity: 'error' });
      })
      .finally(() => setIsLoading(false));
  }, [currProject, showSnackbar]);

  const handleRename = async (item, newName) => {
    try {
      const updated = await apiReq(
        `projects/specifications-history/rename?document_id=${item.id}&name=${encodeURIComponent(newName)}`,
        'post'
      );
      setGeneratedList((prev) =>
        prev.map((el) => (el.id === item.id ? { ...el, name: updated?.name ?? newName } : el))
      );
      showSnackbar({ message: 'Название обновлено', severity: 'success' });
    } catch {
      showSnackbar({ message: 'Ошибка при переименовании', severity: 'error' });
    }
  };

  const handleDelete = async (item) => {
    globalConfirm({
      message: 'Удалить это ТЗ?',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
      onConfirm: async () => {
        setIsLoading(true);
        try {
          const res = await apiReq(
            `projects/specifications-history/delete?document_id=${item.id}`,
            'delete'
          );
          if (res && res.status && res.status >= 400) {
            showSnackbar({ message: 'Ошибка при удалении', severity: 'error' });
          } else {
            showSnackbar({ message: 'ТЗ успешно удалено', severity: 'success' });
            getGeneratedList();
          }
        } catch (err) {
          showSnackbar({ message: 'Ошибка при удалении', severity: 'error' });
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  useEffect(() => {
    if (currProject) {
      getGeneratedList();
    }
  }, [currProject, getGeneratedList, reloadKey]);

  return (
    <>
      {!currProject && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Выберите текущий проект
        </Alert>
      )}
      {currProject && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? 0.75 : 2,
            wordBreak: 'break-word',
            lineHeight: 1.45,
            minHeight: 0,
          }}
        >
          {!compact ? (
            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Список сгенерированных ТЗ
            </Typography>
          ) : null}
          <Box sx={{ minHeight: 0 }}>
            {isLoading ? (
              <Loader />
            ) : generatedList.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: compact ? 0.75 : 1.25,
                }}
              >
                {generatedList.map((item) => (
                  <GeneratedListItem
                    key={item.id}
                    item={item}
                    selected={selectedTechnicalTask && selectedTechnicalTask.id === item.id}
                    onSelect={onSelectItem}
                    onDelete={handleDelete}
                    onDownload={handleDownload}
                    onRename={handleRename}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Нет сгенерированных ТЗ
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}
